import { NavLink } from 'react-router-dom';
import { Star } from '../../icons';
import { useFavoritesStore, type FavoriteItem } from '../../store/favoritesStore';
import { useMenuCatalog, findCatalogItem, type MenuCatalogItem } from '../../lib/menu-catalog';

/**
 * ⑤ 우측 레일에 고정된 메뉴 목록 — 2026-08-19 사용자 지시.
 *
 *   "전체메뉴에서 메뉴명에 마우스를 올리면 … 별모양 … 클릭하면 우측 레일에 해당 메뉴 표시"
 *   "우측 레일을 72px 로 좌측 레일과 동일한 디자인 구조로"
 *
 * 종전 `FavoritesRailItems` 는 라벨 없는 32px 사각 아이콘이었고, 그 위에 '즐겨찾기' 라벨과
 * 별 버튼(팝오버 진입점)이 얹혀 있었다. 지시대로 그 진입점을 걷어내고 — 등록·해제는 이제
 * 전체메뉴의 별이 전담한다 — 목록만 **좌측 레일 항목과 같은 모양**(아이콘 위 / 라벨 아래)으로
 * 다시 세운다. 그래야 양쪽 레일이 같은 규칙으로 읽힌다.
 *
 * ⚠ 새 컴포넌트인 이유: `FavoritesRailItems` 는 롤백 경로(`VITE_RAIL_MENU_PIN='0'`)에서
 *   종전 모양 그대로 살아 있어야 한다. 그래서 고치지 않고 옆에 새로 세웠다.
 *
 * 아이콘·라벨은 `useMenuCatalog()`(= /menu-config 레지스트리 → NavBar fallback)에서 찾는다 —
 * 관리자가 메뉴 이름·아이콘을 바꾸면 여기도 따라간다. 카탈로그에 없는 경로(메뉴가 꺼졌거나
 * 이름이 바뀐 뒤)는 **등록 당시 라벨 + 별 아이콘**으로 남긴다: 조용히 사라지면 왜 없어졌는지
 * 알 수 없다.
 */
export function RailPinnedMenus(): JSX.Element | null {
  const items = useFavoritesStore((s) => s.items);
  const catalog = useMenuCatalog();

  // 하나도 없으면 아무 것도 그리지 않는다 — 빈 칸이 레일 세로 예산을 먹지 않게.
  if (items.length === 0) return null;

  return (
    <ul className="app-rightrail__pinlist" aria-label="고정한 메뉴">
      {items.map((fav) => (
        <RailPinnedItem key={fav.to} fav={fav} meta={findCatalogItem(catalog, fav.to)} />
      ))}
    </ul>
  );
}

/** 항목 하나 — 좌측 레일의 `.navbar-item` 과 같은 리듬(아이콘 위 / 10px 라벨 아래). */
function RailPinnedItem({ fav, meta }: { fav: FavoriteItem; meta: MenuCatalogItem | null }) {
  // 라벨은 카탈로그가 있으면 그쪽을 쓴다(관리자가 이름을 바꿔도 따라간다).
  const label = meta?.label ?? fav.label;
  const Icon = meta?.icon ?? Star;
  const icon = meta?.svg
    ? <span className="app-rightrail__pinsvg" aria-hidden dangerouslySetInnerHTML={{ __html: meta.svg }} />
    : <Icon size={20} aria-hidden />;

  const inner = (
    <>
      {icon}
      <span className="app-rightrail__pinlabel">{label}</span>
    </>
  );

  // 외부 링크는 새 탭(레지스트리 is_external 과 같은 규칙) — 라우터로 보내면 앱이 깨진다.
  if (fav.external) {
    return (
      <li>
        <a
          className="app-rightrail__pinitem"
          href={fav.to}
          target="_blank"
          rel="noopener noreferrer"
          title={label}
          aria-label={label}
        >
          {inner}
        </a>
      </li>
    );
  }

  return (
    <li>
      <NavLink
        to={fav.to}
        className={({ isActive }) => ['app-rightrail__pinitem', isActive && 'is-active'].filter(Boolean).join(' ')}
        title={label}
        aria-label={label}
      >
        {inner}
      </NavLink>
    </li>
  );
}
